<template>
    <admin-layout 
    title="Individual Patient Bookings"
    show-back
    backUrl="/reports">
        <div>
            <TabComponent
            :button-items="true"
            >
                <template #buttons>
                    <div class="py-3">
                        <ButtonComponent
                        @click="exportIndividualBookings()"
                        class="text-gray-900"
                        secondary
                        exportBtn>
                            Export
                        </ButtonComponent>
                    </div>
                </template>
            </TabComponent>
        </div>
        <!-- Filter -->
        <div class="px-6 py-3">
            <FilterComponent
                :text-filters="false"
                @update:filters="applyFilters"
                textFilters
                :search="searchText"
                @fetchData="searchData"
            >
                <template #fields>
                    <div class="space-y-6">
                        <DatePicker
                            id="dateRange"
                            name="dateRange"
                            range
                            is24
                            isPresetRanges
                            v-model="filters.dateRange"
                            placeholder="Booking Date"
                            label="Booking Date"
                        />
                        <DatePicker
                            id="createdRange"
                            name="createdRange"
                            range
                            is24
                            isPresetRanges
                            v-model="filters.createdRange"
                            placeholder="Created Date"
                            label="Created Date"
                        />
                        <MultiselectInput
                            id="timePeriod"
                            name="timePeriod"
                            label="Time Period"
                            v-model="filters.timePeriod"
                            placeholder="Time Period"
                            :options="timePeriods"
                        />
                        <MultiselectInput
                            v-model="filters.patientID"
                            id="patientID"
                            name="patientID"
                            label="Patient ID"
                            placeholder="Patient ID"
                            mode="tags"
                            :options="patientIDs"
                        />

                        <MultiselectInput
                            v-model="filters.packageID"
                            id="packageID"
                            name="packageID"
                            label="Package ID"
                            placeholder="Package ID"
                            mode="tags"
                            :options="packageIDs"
                        />
                        <MultiselectInput
                            v-model="filters.riderID"
                            id="riderID"
                            name="riderID"
                            label="Rider ID"
                            placeholder="Rider ID"
                            mode="tags"
                            :options="riderIDs"
                        />
                        <MultiselectInput
                            id="bookingStatus"
                            name="bookingStatus"
                            label="Booking Status"
                            v-model="filters.bookingStatus"
                            placeholder="Booking Status"
                            :options="bookingStatuses"
                        />
                        <!-- <MultiselectInput
                            id="bookingType"
                            name="bookingType"
                            label="Booking Type"
                            v-model="filters.bookingType"
                            placeholder="Booking Type"
                            :options="bookingTypes"
                        /> -->
                    </div>
                </template>
            </FilterComponent>
        </div>

        <div class="">
            <DataTable 
            :headers="headers" 
            :noAction="true"
            :count="items?.total">
                <template #body>
                    <tr v-for="(individualBooking, key) in items?.data" :key="key">
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">{{ individualBooking.patient?.patient_id }}</td>
                        <td class="w-8/12 px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ `${individualBooking.patient?.first_name} ${individualBooking.patient?.last_name}` }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ individualBooking.package_name }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 break-all whitespace-nowrap">
                            {{ individualBooking.riders }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ formatter.format(individualBooking.total_fee) }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ individualBooking.booking_type }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            <StatusPill
                            :color="getStatusColor(individualBooking.status)" 
                            :text="individualBooking.status"/>
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ `${formatDate(individualBooking.booking_date)} (${formatTime(individualBooking.booking_start_time)} - ${formatTime(individualBooking.booking_end_time)})` }}
                        </td>
                        <td class="px-6 py-5 text-sm text-gray-900 whitespace-nowrap">
                            {{ `${formatDate(individualBooking.booking_created_date)} (${formatTime(individualBooking.booking_created_date)})` }}
                        </td>
                    </tr>
                </template>
            </DataTable>
            <PagePagination class="mb-6" 
                :items="items"  
                @nextPage="fetchIndividualBookings"
            />
        </div>

        <PageLoader v-if="loader" />

        <SuccessModal
            :show="showSuccess"
            @cancel="showSuccess = false"
            @confirm="showSuccess = false"
            action-text="Close"
            title="Individual Bookings Export"
            content="Export is being processed. Please check your registered email."
        />

        <ErrorModal
            width="w-[548px]"
            title="Error"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
        >
            <template #content>
                {{ errorMsg }}
            </template>
        </ErrorModal>

    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import FilterComponent from '@/components/FilterComponent.vue';
import DataTable from '@/components/DataTable.vue';
import TabComponent from '@/components/TabComponent.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import StatusPill from '@/components/StatusPill.vue';
import PageLoader from "@/components/PageLoader.vue";
import ErrorModal from '@/components/ErrorModal.vue';
import PagePagination from '@/components/PagePagination.vue';
import DatePicker from '@/components/inputs/DatePicker.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import MultiselectInput from '@/components/inputs/MultiselectInput.vue';
import { ref, onMounted, watch } from 'vue';
import { paginatePage } from '@/helpers/PageHelper';
import ReportService from '@/classes/ReportService';
import { startOfYear, endOfYear } from 'date-fns';
import DropdownData from '@/classes/DropdownData';
import { formatDate, formatTime } from '@/helpers/TimeFormatter'
import { formatter } from '@/helpers/NumberHelper'
import throttle from "lodash/throttle";


interface Patient {
    first_name: string,
    last_name: string
}

interface individualBooking {
    patient: Patient
    booking_type: string,
    status: string,
    booking_date: Date,
    booking_start_time: Date,
    booking_end_time: Date,
    booking_created_date: Date
}

interface Item {
    data: individualBooking[]
    total: number
}

const loader      = ref(false);
const showSuccess = ref(false);
const showError   = ref(false);
const errorMsg    = ref();
const goToPage    = ref();
const packageIDs    = ref();
const riderIDs    = ref();
const patientIDs    = ref();
const searchText  = ref();

const items = ref<Item>({
    data: [],
    total: 0
});

const filters = ref({
    dateRange: [
        startOfYear(new Date).toISOString(),
        endOfYear(new Date).toISOString(),
    ],
    createdRange: [
        startOfYear(new Date).toISOString(),
        endOfYear(new Date).toISOString(),
    ],
    bookingType: 'All',
    bookingStatus: 'All',
    timePeriod: 'year',
    patientID: null,
    packageID: null,
    riderID: null,
})

const bookingTypes = [
    { id: '1', label: 'All', value: 'All' },
    { id: '2', label: 'New', value: 'New' },
    { id: '3', label: 'Rescheduled', value: 'Rescheduled' },
];

const bookingStatuses = [
    { id: '1', label: 'All', value: 'All'},
    { id: '2', label: 'Booked', value: 'Booked'},
    { id: '3', label: 'Cancelled', value: 'Cancelled'},
    { id: '4', label: 'No-Show', value: 'No-Show'},
    { id: '5', label: 'Done', value: 'Done'},
];

const timePeriods = [
    { id: '1', label: '24 hours', value: 'day'},
    { id: '2', label: '1 week', value: 'week'},
    { id: '3', label: '1 month', value: 'month'},
    { id: '4', label: '1 year', value: 'year'},
];

const headers: { text: string }[] = [
    { text: 'Patient ID' }, 
    { text: 'Patient Name' },
    { text: 'Package' },
    { text: 'Rider/s' },
    { text: 'Amount' },
    { text: 'Booking Type' },
    { text: 'Status' },
    { text: 'Booking Date and Time' },
    { text: 'Date Created'}
];

const getStatusColor = (status: string) => {
    if(status) {
        let color = null;
        switch (status) {
            case 'Booked':
                color = 'blue';
                break;
        
            case 'Cancelled/No-Show':
            case 'Cancelled':
                color = 'red';
                break;
        
            case 'Done':
                color = 'green';
                break;
            
            default:
                color = 'yellow'
                break;
        }

        return color;
    }

    return 'yellow';
}

const applyFilters = () => {
    fetchIndividualBookings(goToPage.value);
}

const fetchIndividualBookings = async (data: string) => {
    try {
        loader.value = true;

        const startDate     = filters.value.dateRange ? `&startDate=${filters.value.dateRange[0]}` : '';
        const endDate       = filters.value.dateRange ? `&endDate=${filters.value.dateRange[1]}` : '';
        const createdStartDate     = filters.value.createdRange ? `&createdStartDate=${filters.value.createdRange[0]}` : '';
        const createdEndDate       = filters.value.createdRange ? `&createdEndDate=${filters.value.createdRange[1]}` : '';
        const bookingType   = filters.value.bookingType ? `&bookingType=${filters.value.bookingType}` : '';
        const bookingStatus = filters.value.bookingStatus ? `&bookingStatus=${filters.value.bookingStatus}` : '';
        const timePeriod    = filters.value.timePeriod ? `&timePeriod=${filters.value.timePeriod}` : '';
        const patientID    = filters.value.patientID ? `&patientID=${filters.value.patientID}` : '';
        const packageID    = filters.value.packageID ? `&packageID=${filters.value.packageID}` : '';
        const riderID    = filters.value.riderID ? `&riderID=${filters.value.riderID}` : '';
        const queryString = searchText.value ? `&search_key=${searchText.value}` : '';
        
        goToPage.value = paginatePage(data)

        const response   = await ReportService.fetchIndividualBookings(`${goToPage.value}${startDate}${endDate}${createdStartDate}${createdEndDate}${bookingType}${bookingStatus}${timePeriod}${patientID}${packageID}${riderID}${queryString}`);
        loader.value = false;
        
        items.value = response.data;
    } catch (error: any) {
            loader.value    = false;
            showError.value = true;
            errorMsg.value  = error;

            if(error.response.status == 401) {
                window.location.replace('/login');
            }
    }
    finally{
        loader.value = false
    }
}

const exportIndividualBookings = async () => {
    try {
        loader.value = true;

        const startDate     = filters.value.dateRange ? `&startDate=${filters.value.dateRange[0]}` : '';
        const endDate       = filters.value.dateRange ? `&endDate=${filters.value.dateRange[1]}` : '';
        const createdStartDate     = filters.value.createdRange ? `&createdStartDate=${filters.value.createdRange[0]}` : '';
        const createdEndDate       = filters.value.createdRange ? `&createdEndDate=${filters.value.createdRange[1]}` : '';
        const bookingType   = filters.value.dateRange ? `&bookingType=${filters.value.bookingType}` : '';
        const bookingStatus = filters.value.dateRange ? `&bookingStatus=${filters.value.bookingStatus}` : '';
        const timePeriod    = filters.value.dateRange ? `&timePeriod=${filters.value.timePeriod}` : '';
        const patientID    = filters.value.patientID ? `&patientID=${filters.value.patientID}` : '';
        const packageID    = filters.value.packageID ? `&packageID=${filters.value.packageID}` : '';
        const riderID    = filters.value.riderID ? `&riderID=${filters.value.riderID}` : '';
        const queryString = searchText.value ? `&search_key=${searchText.value}` : '';

        await ReportService.exportIndividualBookings(`${startDate}${endDate}${createdStartDate}${createdEndDate}${bookingType}${bookingStatus}${timePeriod}${patientID}${packageID}${riderID}${queryString}`);

        showSuccess.value = true;
    } catch (error: any) {
        showError.value = true;
        errorMsg.value  = error.response?.data.message;
    } finally {
        loader.value = false;
    }
};

const fetchPackages = async () => {
    loader.value = true

    await DropdownData.getPackages()
        .then((result: any) => {

            packageIDs.value = result.data.response;

            loader.value = false;
        })
        .catch((error) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = error;

            if(error.response.status == 401) {
                window.location.replace('/login')
            }
        })
}

const fetchRiders = async () => {
    loader.value = true

    await DropdownData.getRiders()
        .then((result: any) => {

            riderIDs.value = result.data.response;
            
            loader.value = false;
        })
        .catch((error) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = error;

            if(error.response.status == 401) {
                window.location.replace('/login')
            }
        })
}

const fetchPatients = async () => {
    loader.value = true

    await DropdownData.getPatients()
        .then((result: any) => {

            patientIDs.value = result.data.response;
            
            loader.value = false;
        })
        .catch((error) => {
            loader.value = false;
            showError.value = true;
            errorMsg.value = error;

            if(error.response.status == 401) {
                window.location.replace('/login')
            }
        })
}

const searchData = (val: string) => {
    searchText.value = val
}

/**
 * ********************************
 * Wathchers
 * ********************************
 */
watch(
    searchText,
    throttle(() => {
        fetchIndividualBookings(goToPage.value)
    }, 1000)
);

onMounted(async() => {
    await fetchIndividualBookings(goToPage.value); 
    await fetchPackages();
    await fetchRiders();
    await fetchPatients();
})
</script>